import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteId } from '@store/selectors/site.selectors';
import type { IAdminManager } from '@apis/adminManagers';
import { Button } from '@components/ui/Button/Button';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { PlusIcon, SearchIcon } from '@/utils/icons';
import {
  useAdminManagers,
  useCreateAdminManager,
  useDeleteAdminManager,
  useManagementTrades,
  useUpdateAdminManager,
} from './hooks/useAdminManagers';
import { ManagersTable } from './ManagersTable/ManagersTable';
import { roleLabel } from './ManagersTable/roleLabel';
import { ManagerDialog } from './ManagerDialog/ManagerDialog';
import { DeleteManagerDialog } from './DeleteManagerDialog/DeleteManagerDialog';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import styles from '../AdminPage.module.css';

export function ManagementTab() {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId);
  const { toasts, push, dismiss } = useToasts();

  const { data, isLoading, isError } = useAdminManagers(siteId ?? null);
  const managers = useMemo(() => data?.managers ?? [], [data]);
  const { data: tradesData } = useManagementTrades(siteId ?? null);
  const trades = useMemo(() => tradesData?.trades ?? [], [tradesData]);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<IAdminManager | null>(null);
  const [deleting, setDeleting] = useState<IAdminManager | null>(null);

  const createMutation = useCreateAdminManager(siteId ?? null, push);
  const updateMutation = useUpdateAdminManager(siteId ?? null, push);
  const deleteMutation = useDeleteAdminManager(siteId ?? null, push);

  // Search matches full name + translated role.
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return managers;
    return managers.filter((m) =>
      [m.name, roleLabel(t, m)].some((v) => v.toLowerCase().includes(query)),
    );
  }, [managers, search, t]);

  if (!siteId) return null;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <PageHeader
          title={t('adminManagement.title')}
          subtitle={t('adminManagement.subtitle')}
        />
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchIcon size={15} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('adminManagement.searchPlaceholder')}
            />
          </div>
          <span className={styles.count}>
            {t('adminManagement.count', { count: visible.length })}
          </span>
          <div className={styles.toolbarSpacer} />
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <PlusIcon size={14} />
            {t('adminManagement.add')}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <ManagersTable
          managers={visible}
          emptyMessage={
            isError
              ? t('adminManagement.loadError')
              : isLoading
                ? t('adminManagement.loading')
                : search.trim()
                  ? t('adminManagement.noMatches')
                  : t('adminManagement.empty')
          }
          onEdit={setEditing}
          onDelete={setDeleting}
        />
      </div>

      {createOpen && (
        <ManagerDialog
          open
          manager={null}
          trades={trades}
          submitting={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(draft) => {
            if (createMutation.isPending) return;
            createMutation.mutate(
              { tradeId: draft.tradeId, name: draft.name, phone: draft.phone || null },
              { onSuccess: () => setCreateOpen(false) },
            );
          }}
        />
      )}

      {editing && (
        <ManagerDialog
          key={editing.id}
          open
          manager={editing}
          trades={trades}
          submitting={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(draft) => {
            if (updateMutation.isPending) return;
            updateMutation.mutate(
              { managerId: editing.id, phone: draft.phone || null },
              { onSuccess: () => setEditing(null) },
            );
          }}
        />
      )}
      
      {deleting && (
        <DeleteManagerDialog
          key={deleting.id}
          manager={deleting}
          deleting={deleteMutation.isPending}
          onConfirm={(manager) => deleteMutation.mutateAsync(manager.id).then(() => setDeleting(null))}
          onClose={() => setDeleting(null)}
        />
      )}

      <Toasts toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}